import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { claimErrorText, errorText } from '../lib/errors';
import type { ClaimResult } from '../lib/types';
import { ErrorNote, LOGO_SRC, Spinner } from '../components/ui';
import { EmailCode } from '../components/EmailCode';
import { GoogleSignIn } from '../components/GoogleSignIn';

const KEY = 'one10:claim';

function loadClaim(): ClaimResult | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as ClaimResult) : null;
  } catch {
    return null;
  }
}

function saveClaim(r: ClaimResult) {
  try { sessionStorage.setItem(KEY, JSON.stringify(r)); } catch { /* private mode */ }
}

/**
 * /join#<token>: a one-time join link, from a family member (WhatsApp or copied) or from the Food Committee (WhatsApp,
 * or a QR at the help desk or counter). Shows whose family it is, then adds and verifies an email and joins it.
 */
export default function Claim() {
  const { session, me, refreshMe, signOut } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [found, setFound] = useState<ClaimResult | null>(loadClaim);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Set when the email code was just verified here, so the link step runs straight away.
  const [autoLink, setAutoLink] = useState(false);
  const started = useRef(false);
  const { pathname, hash } = useLocation();
  const joinToken = pathname === '/join' ? hash.slice(1) : '';
  const [joining, setJoining] = useState(!!joinToken);
  const [joinError, setJoinError] = useState<string | null>(null);

  // A join link: check it, then carry on as if the booking had been found. The token leaves the address bar.
  useEffect(() => {
    if (!joinToken) return;
    history.replaceState(null, '', '/join');
    api.claimPeek(joinToken).then((r) => {
      if (r.ok) { setFound(r); saveClaim(r); } else setJoinError(claimErrorText(r.error));
    }, (e) => setJoinError(errorText(e))).finally(() => setJoining(false));
  }, [joinToken]);

  async function complete() {
    if (!found?.token) return;
    setBusy(true);
    setError(null);
    try {
      await api.claimComplete(found.token);
      try { sessionStorage.removeItem(KEY); } catch { /* ignore */ }
      await refreshMe();
      await Promise.all([qc.invalidateQueries({ queryKey: ['dashboard'] }), qc.invalidateQueries({ queryKey: ['history'] })]);
      navigate('/', { replace: true });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (session && autoLink && found && !started.current) {
      started.current = true;
      void complete();
    }
  });

  const foundCard = found && (found.family ? (
    <div className="notice ok">
      <p><b>{found.name ? `${found.name} invited you` : "You're invited"} to their family</b></p>
      <p>Flat {found.flat}: you'll see the family's bookings and passes and can book for them.</p>
    </div>
  ) : (
    <div className="notice ok">
      <p><b>Found your booking</b></p>
      <p>{[found.order_code, `flat ${found.flat}`, found.name].filter(Boolean).join(' · ')}</p>
    </div>
  ));

  if (joining) return <div className="shell"><div className="auth"><div className="empty"><Spinner /></div></div></div>;

  if (!found) {
    return (
      <div className="shell">
        <div className="auth">
          <span className="brand-chip"><img src={LOGO_SRC} alt="ONE10" style={{ height: 40 }} /></span>
          <h1>Join link</h1>
          <ErrorNote error={joinError ?? 'Open the link from the WhatsApp message again, or scan the join QR.'} />
          <Link className="btn primary block lg" to="/signin">Sign in</Link>
          <p className="fine">No link? Sign in and enter your flat: you can ask your family there to let you in.</p>
        </div>
      </div>
    );
  }

  if (session) {
    return (
      <div className="shell">
        <div className="auth">
          {foundCard}
          <h1>{found.family ? 'Join the family' : 'Link this email'}</h1>
          <p className="muted">You're signed in as <b>{me?.email}</b>. {found.family ? `Join the family at flat ${found.flat} with it?` : `Link it to flat ${found.flat}?`}</p>
          <ErrorNote error={error} />
          <button className="btn primary block lg" disabled={busy} onClick={() => void complete()}>
            {busy ? 'Linking…' : found.family ? 'Join the family' : `Link to ${found.flat}`}
          </button>
          <button className="link-btn" onClick={() => void signOut()}>Use a different email</button>
          <button className="link-btn" onClick={() => { setFound(null); try { sessionStorage.removeItem(KEY); } catch { /* ignore */ } }}>Start again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="shell">
      <EmailCode
        head={(
          <>
            {foundCard}
            <h1>Add your email</h1>
            <p className="muted">
              From now on you sign in with this email, and payment updates and meal passes are emailed to it.
              We'll send a 6-digit code to check it's yours.
            </p>
          </>
        )}
        alt={<GoogleSignIn next="/join" />}
        codeHead={foundCard}
        submitLabel="Email me a code"
        onSignedIn={() => setAutoLink(true)}
        footer={<button type="button" className="link-btn" onClick={() => { setFound(null); try { sessionStorage.removeItem(KEY); } catch { /* ignore */ } }}>Start again</button>}
      />
    </div>
  );
}
